import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Droplets, Gem, Lock, Package, Search, ShoppingBasket, Sparkles, User, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type PackageKey = "pin" | "isim" | "normal" | "premium" | "iliski" | "test" | string;

type Package = {
  id: string;
  key: PackageKey;
  name: string;
  description: string;
  basePriceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  imageUrl?: string;
};

type ShopProduct = {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: "stone" | "oil" | "bundle" | "other";
  priceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  tags: string[];
};

type DigitalItem = {
  id: string;
  title?: string;
  baslik?: string;
  description?: string;
  aciklama?: string;
  duration_minutes?: number;
  sure_saniye?: number;
  cover_image_url?: string;
  gorsel_url?: string;
  price: number;
  tags?: string[];
};

type CatalogResponse = {
  ok: true;
  packages: Package[];
  products: ShopProduct[];
  settings: unknown;
  digitalContent?: {
    meditations: DigitalItem[];
    breaths: DigitalItem[];
    frekans: DigitalItem[];
    diets?: DigitalItem[];
    rituals?: DigitalItem[];
  };
};

type CartItem = {
  packageKey: PackageKey;
  qty: number;
};

const GOLD = "#d4af37";
const CART_STORAGE_KEY = "numeroloji_cart_v1";

function safeParseCart(raw: string | null): CartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((it) => {
        const obj = it as any;
        const packageKey = typeof obj?.packageKey === "string" ? (obj.packageKey as PackageKey) : null;
        const qty = typeof obj?.qty === "number" ? obj.qty : Number(obj?.qty);
        if (!packageKey) return null;
        if (!Number.isFinite(qty) || qty <= 0) return null;
        return { packageKey, qty: Math.min(99, Math.trunc(qty)) } satisfies CartItem;
      })
      .filter(Boolean) as CartItem[];
  } catch {
    return [];
  }
}

function loadCart(): CartItem[] {
  return safeParseCart(globalThis?.localStorage?.getItem(CART_STORAGE_KEY) ?? null);
}

function saveCart(items: CartItem[]) {
  globalThis?.localStorage?.setItem(CART_STORAGE_KEY, JSON.stringify(items));
}

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await fetch("/api/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

type SubscriptionPlan = {
  monthly_price: number;
  yearly_price: number;
  yearly_savings_percent: number;
  features?: string[];
};

async function fetchSubscriptionPlan(): Promise<SubscriptionPlan | null> {
  const res = await fetch("/api/admin/subscription-plans/public");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.plan) return null;
  return json.plan as SubscriptionPlan;
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ₺`;
  }
}

function normalizeTag(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function isKarmicTagged(tags: string[]) {
  const keys = new Set((tags ?? []).filter(Boolean).map(normalizeTag));
  for (const k of keys) {
    if (k.startsWith("karmikborc")) return true;
  }
  return false;
}

function productIcon(category: ShopProduct["category"]) {
  switch (category) {
    case "stone":
      return Gem;
    case "oil":
      return Droplets;
    case "bundle":
      return Package;
    default:
      return Sparkles;
  }
}

function getPackageEffectivePrice(p: Package) {
  return typeof p.discountedPriceTry === "number" && p.discountedPriceTry > 0 ? p.discountedPriceTry : p.basePriceTry;
}

function getProductEffectivePrice(p: ShopProduct) {
  return typeof p.discountedPriceTry === "number" && p.discountedPriceTry > 0 ? p.discountedPriceTry : p.priceTry;
}

function productArt(category: ShopProduct["category"]) {
  switch (category) {
    case "stone":
      return "[background:radial-gradient(circle_at_30%_30%,rgba(212,175,55,0.28)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "oil":
      return "[background:radial-gradient(circle_at_30%_30%,rgba(127,13,242,0.30)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "bundle":
      return "[background:radial-gradient(circle_at_30%_30%,rgba(212,175,55,0.22)_0%,transparent_55%),radial-gradient(circle_at_70%_70%,rgba(127,13,242,0.18)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]";
    default:
      return "[background:radial-gradient(circle_at_30%_30%,rgba(148,163,184,0.18)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
  }
}

type CategoryKey = "all" | "personal" | "relationship" | "digital";

const CATEGORIES: Array<{ key: CategoryKey; label: string; include: (k: PackageKey) => boolean }> = [
  { key: "all", label: "Tümü", include: () => true },
  {
    key: "personal",
    label: "Kişisel Analizler",
    include: (k) => k === "pin" || k === "isim" || k === "normal" || k === "premium",
  },
  { key: "relationship", label: "İlişki Analizleri", include: (k) => k === "iliski" },
  { key: "digital", label: "Dijital İçerikler", include: () => false },
];

function cardArt(key: PackageKey) {
  switch (key) {
    case "pin":
      return "[background:radial-gradient(circle_at_25%_25%,rgba(212,175,55,0.30)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "isim":
      return "[background:radial-gradient(circle_at_65%_30%,rgba(127,13,242,0.32)_0%,transparent_55%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "iliski":
      return "[background:radial-gradient(circle_at_50%_20%,rgba(212,175,55,0.22)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]";
    case "premium":
      return "[background:radial-gradient(circle_at_40%_25%,rgba(212,175,55,0.25)_0%,transparent_60%),radial-gradient(circle_at_70%_65%,rgba(127,13,242,0.22)_0%,transparent_65%),linear-gradient(145deg,#151518,#0a0a0c)]";
    default:
      return "[background:radial-gradient(circle_at_40%_25%,rgba(127,13,242,0.22)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]";
  }
}

export default function NumerolojiMagaza() {
  const navigate = useNavigate();

  const catalogQuery = useQuery({
    queryKey: ["numeroloji-catalog"],
    queryFn: fetchCatalog,
    staleTime: 60_000,
  });

  const planQuery = useQuery({
    queryKey: ["subscription-plan"],
    queryFn: fetchSubscriptionPlan,
    staleTime: 5 * 60_000,
  });

  const packages = useMemo(
    () => (catalogQuery.data?.packages ?? []).filter((p) => p.active && p.key !== "test"),
    [catalogQuery.data],
  );

  const [query, setQuery] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [category, setCategory] = useState<CategoryKey>("all");
  const [digitalTab, setDigitalTab] = useState<"meditation" | "breath" | "frekans" | "diet" | "ritual">("meditation");

  const [cart, setCart] = useState<CartItem[]>(() => loadCart());

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const priceRange = useMemo(() => {
    const prices = packages.map((p) => getPackageEffectivePrice(p)).filter((n) => Number.isFinite(n));
    if (!prices.length) return { min: 0, max: 0 };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [packages]);

  const cartCount = cart.reduce((acc, it) => acc + (Number.isFinite(it.qty) ? it.qty : 0), 0);

  const digitalCount =
    (catalogQuery.data?.digitalContent?.meditations?.length ?? 0) +
    (catalogQuery.data?.digitalContent?.breaths?.length ?? 0) +
    (catalogQuery.data?.digitalContent?.frekans?.length ?? 0) +
    (catalogQuery.data?.digitalContent?.diets?.length ?? 0) +
    (catalogQuery.data?.digitalContent?.rituals?.length ?? 0);

  const categoriesWithCounts = useMemo(() => {
    const map = new Map<CategoryKey, number>();
    for (const c of CATEGORIES) map.set(c.key, 0);
    for (const p of packages) {
      for (const c of CATEGORIES) {
        if (c.key === "all" || c.key === "digital") continue;
        if (c.include(p.key)) map.set(c.key, (map.get(c.key) ?? 0) + 1);
      }
    }
    map.set("all", packages.length);
    map.set("digital", digitalCount);
    return map;
  }, [packages, digitalCount]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const min = Number(minPrice);
    const max = Number(maxPrice);
    const hasMin = Number.isFinite(min) && minPrice.trim() !== "";
    const hasMax = Number.isFinite(max) && maxPrice.trim() !== "";

    const cat = CATEGORIES.find((c) => c.key === category) ?? CATEGORIES[0];

    const rows = packages.filter((p) => {
      if (!cat.include(p.key)) return false;
      if (q) {
        const hay = `${p.name} ${p.description} ${p.key}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const effectivePrice = getPackageEffectivePrice(p);
      if (hasMin && effectivePrice < min) return false;
      if (hasMax && effectivePrice > max) return false;
      return true;
    });

    // Default sort: price descending
    rows.sort((a, b) => {
      const diff = Number(getPackageEffectivePrice(b) || 0) - Number(getPackageEffectivePrice(a) || 0);
      if (diff) return diff;
      return String(a.name || "").localeCompare(String(b.name || ""), "tr");
    });

    return rows;
  }, [category, maxPrice, minPrice, packages, query]);

  const cartItemsResolved = useMemo(() => {
    const byKey = new Map(packages.map((p) => [p.key, p] as const));
    return cart
      .map((it) => {
        const pkg = byKey.get(it.packageKey);
        if (!pkg) return null;
        return { item: it, pkg };
      })
      .filter(Boolean) as Array<{ item: CartItem; pkg: Package }>;
  }, [cart, packages]);

  const cartTotal = useMemo(() => {
    return cartItemsResolved.reduce((sum, row) => sum + getPackageEffectivePrice(row.pkg) * row.item.qty, 0);
  }, [cartItemsResolved]);

  function toggleCart(pkg: Package) {
    setCart((prev) => {
      const exists = prev.find((it) => it.packageKey === pkg.key);
      if (exists) return prev.filter((it) => it.packageKey !== pkg.key);
      return [...prev, { packageKey: pkg.key, qty: 1 }];
    });
  }

  function removeFromCart(pkgKey: PackageKey) {
    setCart((prev) => prev.filter((it) => it.packageKey !== pkgKey));
  }

  const checkoutDisabled = cartItemsResolved.length === 0;

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>

          <div className="flex items-center gap-6 sm:gap-8">
            <Link
              to="/numeroloji"
              className="hidden font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:block sm:text-sm"
            >
              ANA SAYFA
            </Link>
            <Link
              to="/numeroloji/magaza"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              MAĞAZA
            </Link>


            <Link to="/numeroloji/sepet" className="relative" aria-label="Sepet">
              <ShoppingBasket className="h-5 w-5 text-slate-200" />
              {cartCount ? (
                <span
                  className="absolute -right-2 -top-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-black"
                  style={{ backgroundColor: GOLD }}
                  aria-label={`Sepette ${cartCount} ürün`}
                >
                  {cartCount}
                </span>
              ) : null}
            </Link>

            <Link
              to="/panel"
              className="rounded-full p-2 transition-colors hover:bg-white/5"
              aria-label="Hesabım"
              title="Hesabım"
            >
              <User className="h-5 w-5 text-slate-200" />
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-7xl px-4 py-12">
        <div className="grid grid-cols-12 gap-8">
          <aside className="col-span-12 space-y-8 lg:col-span-3">
            <div className="space-y-8 lg:sticky lg:top-28">
              <div className="rounded-lg border border-[rgba(212,175,55,0.12)] bg-[#1a1225] p-6">
                <h3 className="mb-4 flex items-center font-display text-xs tracking-[0.22em] sm:text-sm">
                  <Search className="mr-2 h-4 w-4" style={{ color: GOLD }} />
                  ÜRÜN ARA
                </h3>
                <div className="flex">
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Ürünlerde ara..."
                    className="h-11 rounded-r-none border-0 bg-black/40 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:ring-1"
                    style={{ outlineColor: GOLD }}
                  />
                  <button
                    type="button"
                    className="h-11 rounded-l-none px-4 font-display text-[11px] font-bold tracking-[0.22em] text-black transition-opacity hover:opacity-90"
                    style={{ backgroundColor: GOLD }}
                    onClick={() => {
                      // live filter; button is only for UX parity
                    }}
                  >
                    ARA
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-[rgba(212,175,55,0.12)] bg-[#1a1225] p-6">
                <h3 className="mb-6 font-display text-xs tracking-[0.22em] sm:text-sm">FİYATA GÖRE FİLTRELE</h3>

                <div className="grid gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="mb-1 text-[11px] uppercase tracking-widest opacity-60">Min</div>
                      <Input
                        inputMode="numeric"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        placeholder={priceRange.min ? String(Math.round(priceRange.min)) : "0"}
                        className="h-10 border-0 bg-black/40 text-sm text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-[11px] uppercase tracking-widest opacity-60">Max</div>
                      <Input
                        inputMode="numeric"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        placeholder={priceRange.max ? String(Math.round(priceRange.max)) : "0"}
                        className="h-10 border-0 bg-black/40 text-sm text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs opacity-70">
                    <span>
                      Fiyat: {formatTry(priceRange.min)} — {formatTry(priceRange.max)}
                    </span>
                    <button
                      type="button"
                      className="text-xs underline underline-offset-4 opacity-70 hover:opacity-100"
                      onClick={() => {
                        setMinPrice("");
                        setMaxPrice("");
                      }}
                    >
                      Sıfırla
                    </button>
                  </div>

                  <button
                    type="button"
                    className="w-full border py-2 text-[11px] font-display tracking-[0.22em] transition-all hover:bg-[rgba(212,175,55,0.10)]"
                    style={{ borderColor: "rgba(212,175,55,0.40)" }}
                    onClick={() => {
                      // live filter; button is only for UX parity
                    }}
                  >
                    FİLTRELE
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-[rgba(212,175,55,0.12)] bg-[#1a1225] p-6">
                <h3 className="mb-4 font-display text-xs tracking-[0.22em] sm:text-sm">KATEGORİLER</h3>
                <ul className="space-y-3 text-sm">
                  {CATEGORIES.filter((c) => c.key !== "all").map((c) => {
                    const count = categoriesWithCounts.get(c.key) ?? 0;
                    const active = category === c.key;
                    return (
                      <li key={c.key}>
                        <button
                          type="button"
                          className={cn(
                            "flex w-full items-center justify-between text-left transition-colors",
                            active
                              ? "text-[rgba(212,175,55,0.95)]"
                              : "hover:text-[rgba(212,175,55,0.95)]",
                          )}
                          onClick={() => setCategory(active ? "all" : c.key)}
                        >
                          <span>{c.label}</span>
                          <span className="opacity-40">({count})</span>
                        </button>
                      </li>
                    );
                  })}

                </ul>
              </div>

              <div className="rounded-lg border border-dashed p-6" style={{ borderColor: "rgba(212,175,55,0.30)" }}>
                <h3 className="mb-6 flex items-center justify-between font-display text-xs tracking-[0.22em] sm:text-sm">
                  SEPET
                  <ShoppingBasket className="h-4 w-4" style={{ color: GOLD }} />
                </h3>

                {!cartItemsResolved.length ? (
                  <div className="text-sm text-slate-300">Sepetin boş. Bir analiz seçip sepete ekle.</div>
                ) : (
                  <div className="space-y-4">
                    {cartItemsResolved.map(({ item, pkg }) => (
                      <div key={pkg.key} className="flex items-center gap-3">
                        <div
                          className={cn(
                            "relative grid h-12 w-12 place-items-center overflow-hidden rounded border",
                            cardArt(pkg.key),
                          )}
                          style={{ borderColor: "rgba(212,175,55,0.12)" }}
                        >
                          {pkg.imageUrl?.trim() ? (
                            <img
                              src={pkg.imageUrl}
                              alt={pkg.name || "Paket görseli"}
                              className="absolute inset-0 h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="font-display text-xs" style={{ color: GOLD }}>
                              {String(pkg.key).toUpperCase()}
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/20" />
                        </div>

                        <div className="flex-1 text-xs">
                          <p className="font-semibold text-slate-100">{pkg.name}</p>
                          <p className="opacity-60">
                            {item.qty} × {formatTry(getPackageEffectivePrice(pkg))}
                          </p>
                        </div>

                        <button
                          type="button"
                          className="opacity-40 transition-colors hover:text-red-400 hover:opacity-100"
                          onClick={() => removeFromCart(pkg.key)}
                          aria-label="Sepetten çıkar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}

                    <div className="border-t pt-4" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
                      <div className="mb-4 flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-[0.22em] opacity-60">Ara Toplam:</span>
                        <span className="font-serif text-sm font-bold italic" style={{ color: GOLD }}>
                          {formatTry(cartTotal)}
                        </span>
                      </div>


                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          asChild
                          variant="outline"
                          className="h-10 rounded-none border-[rgba(212,175,55,0.40)] bg-transparent text-[10px] font-display tracking-[0.22em] text-slate-200 hover:bg-[rgba(212,175,55,0.10)]"
                        >
                          <Link to="/numeroloji/sepet">SEPETİM</Link>
                        </Button>
                        <Button
                          asChild
                          disabled={checkoutDisabled}
                          className="h-10 rounded-none text-[10px] font-display font-bold tracking-[0.22em] text-black"
                          style={{ backgroundColor: GOLD }}
                        >
                          <Link to="/numeroloji/sepet">ÖDEME YAP</Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>

          <div className="col-span-12 lg:col-span-9">
            <div className="mb-12 text-center lg:text-left">
              <h1 className="font-display text-4xl tracking-tight md:text-5xl" style={{ color: GOLD }}>
                Mistik Mağaza
              </h1>
              <p className="mt-4 max-w-2xl font-serif text-sm italic leading-relaxed opacity-70">
                Sayıların enerjisini keşfedin. Kişisel kader planınızdan karmik borçlarınıza kadar her şey evrensel bir düzene
                tabidir. Sizin için hazırlanmış özel analizlerle bu düzenin anahtarına yaklaşın.
              </p>
            </div>

            {catalogQuery.isLoading ? (
              <div className="rounded-xl border p-6 text-sm text-slate-300" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
                Paketler yükleniyor...
              </div>
            ) : null}

            {catalogQuery.isError ? (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                {(catalogQuery.error as Error).message}
              </div>
            ) : null}

            {!catalogQuery.isLoading && !catalogQuery.isError ? (
              <>
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 text-xs opacity-70">
                  <div>
                    {category !== "digital" && (
                      <>Gösterilen: <span className="font-semibold text-slate-100">{filtered.length}</span></>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="hidden sm:inline">Kategori:</span>
                    <select
                      className="h-9 rounded border bg-transparent px-3 text-xs"
                      style={{ borderColor: "rgba(212,175,55,0.18)" }}
                      value={category}
                      onChange={(e) => setCategory(e.target.value as CategoryKey)}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label} ({c.key === "digital" ? digitalCount : (categoriesWithCounts.get(c.key) ?? 0)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {category !== "digital" && <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {filtered.map((p) => {
                    const inCart = cart.some((it) => it.packageKey === p.key);

                    return (
                      <div
                        key={p.id}
                        className={cn(
                          "group overflow-hidden rounded-xl border transition-all duration-300",
                          "hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.22)]",
                        )}
                        style={{
                          background: "linear-gradient(145deg, #151518, #0a0a0c)",
                          borderColor: inCart ? "rgba(212,175,55,0.40)" : "rgba(212,175,55,0.12)",
                        }}
                      >
                        <div className={cn("relative aspect-square overflow-hidden", cardArt(p.key))}>
                          {p.imageUrl?.trim() ? (
                            <img
                              src={p.imageUrl}
                              alt={p.name || "Paket görseli"}
                              className="absolute inset-0 h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : null}
                          <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 [background:linear-gradient(to_top,rgba(10,10,12,0.88),transparent)]" />

                          <div className="absolute bottom-0 left-0 right-0 p-6 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <p className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: GOLD }}>
                              Numerolog Ragıp Alkan
                            </p>
                          </div>

                          {inCart ? (
                            <div className="absolute inset-0 flex items-end p-6">
                              <span
                                className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em]"
                                style={{
                                  borderColor: "rgba(212,175,55,0.50)",
                                  backgroundColor: "rgba(212,175,55,0.18)",
                                  color: GOLD,
                                }}
                              >
                                Sepette
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="p-6 text-center">
                          <Link
                            to={`/numeroloji/magaza/${encodeURIComponent(p.key)}`}
                            className="block font-display text-xl tracking-wide text-slate-100 transition-colors hover:text-[rgba(212,175,55,0.95)]"
                          >
                            {p.name}
                          </Link>
                          <p className="mt-1 line-clamp-2 text-xs text-slate-300/80">{p.description}</p>

                          <div className="mt-4 flex items-end justify-center gap-2">
                            {typeof p.discountedPriceTry === "number" && p.discountedPriceTry > 0 && p.discountedPriceTry < p.basePriceTry ? (
                              <span className="text-xs opacity-50 line-through">{formatTry(p.basePriceTry)}</span>
                            ) : null}
                            <p className="font-serif text-lg italic tracking-wider" style={{ color: GOLD }}>
                              {formatTry(getPackageEffectivePrice(p))}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              if (inCart) {
                                navigate("/numeroloji/sepet");
                                return;
                              }
                              toggleCart(p);
                            }}
                            className={cn(
                              "mt-5 w-full rounded border py-3 text-xs font-display tracking-[0.22em] transition-all duration-300",
                              inCart
                                ? "bg-[rgba(212,175,55,1)] text-black shadow-[0_0_15px_rgba(212,175,55,0.30)]"
                                : "bg-transparent text-slate-200 hover:bg-[rgba(212,175,55,1)] hover:text-black",
                            )}
                            style={{ borderColor: "rgba(212,175,55,0.40)" }}
                          >
                            {inCart ? "SEPETİ GÖRÜNTÜLE" : "SEPETE EKLE"}
                          </button>

                          <div className="mt-3">
                            <Link
                              to={`/numeroloji/magaza/${encodeURIComponent(p.key)}`}
                              className="text-[10px] uppercase tracking-[0.22em] opacity-60 hover:opacity-100"
                            >
                              Detayları incele →
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {!filtered.length ? (
                    <div
                      className="rounded-xl border p-8 text-center text-sm text-slate-300 md:col-span-2 xl:col-span-3"
                      style={{ borderColor: "rgba(212,175,55,0.12)" }}
                    >
                      Bu filtrelerle eşleşen paket bulunamadı.
                    </div>
                  ) : null}
                </div>}

                {category !== "digital" && <section id="urunler" className="mt-14">
                  <div className="mb-6">
                    <h2 className="font-display text-2xl tracking-tight" style={{ color: GOLD }}>
                      Ürünler
                    </h2>
                    <p className="mt-2 max-w-2xl text-xs uppercase tracking-[0.22em] text-slate-300/70">
                      Karmik borç (çakra) etiketli ürünler üyelere özeldir.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {(catalogQuery.data?.products ?? []).filter((p) => p && p.active).length ? (
                      (catalogQuery.data?.products ?? [])
                        .filter((p) => p && p.active)
                        .map((p) => {
                          const locked = isKarmicTagged(p.tags ?? []);
                          const Icon = productIcon(p.category);

                          return (
                            <div
                              key={p.id}
                              className="overflow-hidden rounded-xl border"
                              style={{
                                background: "linear-gradient(145deg, #151518, #0a0a0c)",
                                borderColor: "rgba(212,175,55,0.12)",
                              }}
                            >
                              <div className={cn("relative aspect-square overflow-hidden", productArt(p.category))}>
                                <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-black/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-100 backdrop-blur">
                                  <Icon className="h-4 w-4" style={{ color: GOLD }} />
                                  <span>Ürün</span>
                                </div>

                                {locked ? (
                                  <div className="absolute inset-0 grid place-items-center bg-black/55 p-6 text-center">
                                    <div>
                                      <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
                                        <Lock className="h-6 w-6" style={{ color: GOLD }} />
                                      </div>
                                      <div className="mt-4 font-display text-xs uppercase tracking-[0.22em] text-slate-100">
                                        Üyelere özel
                                      </div>
                                      <div className="mt-2 text-xs text-slate-200/80">Karmik borç etiketli ürünler panelde açılır.</div>
                                      <div className="mt-4">
                                        <Link
                                          to="/panel"
                                          className="inline-flex items-center justify-center rounded border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-black"
                                          style={{ backgroundColor: GOLD, borderColor: "rgba(212,175,55,0.40)" }}
                                        >
                                          Panele giriş
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>

                              <div className="p-6">
                                <div className="font-display text-lg tracking-wide text-slate-100">{p.name}</div>
                                <p className="mt-1 line-clamp-2 text-xs text-slate-300/80">{p.description}</p>

                                <div className="mt-4 flex items-center justify-between">
                                  <div className="flex items-end gap-2">
                                    {typeof p.discountedPriceTry === "number" && p.discountedPriceTry > 0 && p.discountedPriceTry < p.priceTry ? (
                                      <span className="text-xs opacity-50 line-through">{formatTry(p.priceTry)}</span>
                                    ) : null}
                                    <div className="font-serif text-lg italic" style={{ color: GOLD }}>
                                      {formatTry(getProductEffectivePrice(p))}
                                    </div>
                                  </div>
                                  {!locked ? (
                                    <span className="rounded border px-3 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-200/80" style={{ borderColor: "rgba(212,175,55,0.30)" }}>
                                      Yakında
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <div className="rounded-xl border p-8 text-center text-sm text-slate-300 md:col-span-2 xl:col-span-3" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
                        Henüz ürün eklenmemiş.
                      </div>
                    )}
                  </div>
                </section>}

                {(category === "digital" || category === "all") && catalogQuery.data?.digitalContent && (
                  <section id="dijital" className="mt-14">
                    <div className="mb-6">
                      <h2 className="font-display text-2xl tracking-tight" style={{ color: GOLD }}>
                        Dijital İçerikler
                      </h2>
                      <p className="mt-2 max-w-2xl text-xs uppercase tracking-[0.22em] text-slate-300/70">
                        Tek seferlik satın alım ile sınırsız erişim.
                      </p>
                    </div>

                    {/* Premium Üyelik kartları */}
                    <div className="mb-10">
                      <h3 className="mb-4 font-display text-lg tracking-wide text-white">Premium Üyelik</h3>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {(["monthly", "yearly"] as const).map((plan) => {
                          const p = planQuery.data;
                          const price = plan === "monthly" ? (p?.monthly_price ?? 199) : (p?.yearly_price ?? 1499);
                          const savings = p
                            ? Math.round((1 - (p.yearly_price / 12) / p.monthly_price) * 100)
                            : 37;
                          const features = p?.features?.length
                            ? p.features
                            : ["Tüm meditasyon ve nefes içerikleri", "Detaylı numeroloji ve kader haritası", "Seni beğenenleri gör", "Sınırsız çember katılımı"];
                          return (
                            <div
                              key={plan}
                              className="relative overflow-hidden rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.25)]"
                              style={{
                                background: "radial-gradient(circle at 30% 20%, rgba(212,175,55,0.14) 0%, transparent 55%), radial-gradient(circle at 75% 70%, rgba(103,75,181,0.18) 0%, transparent 55%), linear-gradient(145deg,#151518,#0a0a0c)",
                                borderColor: "rgba(212,175,55,0.30)",
                              }}
                            >
                              {plan === "yearly" && (
                                <div
                                  className="absolute right-4 top-4 rounded-full px-2.5 py-0.5 text-[10px] font-bold"
                                  style={{ background: GOLD, color: "#241a00" }}
                                >
                                  %{savings} tasarruf
                                </div>
                              )}
                              <div className="p-6">
                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                                  {plan === "monthly" ? "AYLIK" : "YILLIK"}
                                </p>
                                <p className="font-display mt-1 text-xl tracking-wide text-slate-100">Soul Circles Premium</p>
                                <p className="mt-3 font-serif text-2xl italic" style={{ color: GOLD }}>
                                  ₺{price}
                                  <span className="ml-1 text-sm not-italic text-slate-400">
                                    {plan === "monthly" ? "/ay" : "/yıl"}
                                  </span>
                                </p>
                                <ul className="mt-4 flex flex-col gap-2">
                                  {features.slice(0, 4).map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-xs text-slate-300">
                                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: GOLD }} />
                                      {f}
                                    </li>
                                  ))}
                                </ul>
                                <Link
                                  to={`/numeroloji/premium?plan=${plan}`}
                                  className="mt-5 block w-full rounded border py-3 text-center text-xs font-display tracking-[0.22em] text-slate-200 transition-all duration-300 hover:bg-[rgba(212,175,55,1)] hover:text-black"
                                  style={{ borderColor: "rgba(212,175,55,0.40)" }}
                                >
                                  SATIN AL
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mb-6 flex flex-wrap gap-2">
                      {(["meditation", "breath", "frekans", "diet", "ritual"] as const).map((tab) => {
                        const labels = { meditation: "Meditasyon", breath: "Nefes", frekans: "Frekans", diet: "Diyet", ritual: "Ritüel" };
                        const dc = catalogQuery.data.digitalContent!;
                        const counts = {
                          meditation: dc.meditations.length,
                          breath: dc.breaths.length,
                          frekans: dc.frekans.length,
                          diet: dc.diets?.length ?? 0,
                          ritual: dc.rituals?.length ?? 0,
                        };
                        if (counts[tab] === 0) return null;
                        return (
                          <button
                            key={tab}
                            type="button"
                            onClick={() => setDigitalTab(tab)}
                            className={cn(
                              "rounded border px-4 py-2 text-xs font-display tracking-[0.16em] transition-all",
                              digitalTab === tab
                                ? "text-black"
                                : "bg-transparent text-slate-300 hover:bg-[rgba(212,175,55,0.10)]",
                            )}
                            style={digitalTab === tab ? { backgroundColor: GOLD, borderColor: GOLD } : { borderColor: "rgba(212,175,55,0.30)" }}
                          >
                            {labels[tab]} ({counts[tab]})
                          </button>
                        );
                      })}
                    </div>

                    {(() => {
                      const dc = catalogQuery.data.digitalContent!;
                      const items =
                        digitalTab === "meditation" ? dc.meditations
                        : digitalTab === "breath" ? dc.breaths
                        : digitalTab === "frekans" ? dc.frekans
                        : digitalTab === "diet" ? (dc.diets ?? [])
                        : (dc.rituals ?? []);

                      if (!items.length) {
                        return (
                          <div className="rounded-xl border p-8 text-center text-sm text-slate-300" style={{ borderColor: "rgba(212,175,55,0.12)" }}>
                            Bu kategoride henüz içerik yok.
                          </div>
                        );
                      }

                      return (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                          {items.map((item) => {
                            const title = item.title ?? item.baslik ?? "";
                            const desc = item.description ?? item.aciklama ?? "";
                            const imgUrl = item.cover_image_url ?? item.gorsel_url;
                            const durationMin = item.duration_minutes
                              ? item.duration_minutes
                              : item.sure_saniye
                              ? Math.round(item.sure_saniye / 60)
                              : null;

                            return (
                              <div
                                key={item.id}
                                className="overflow-hidden rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.22)]"
                                style={{
                                  background: "linear-gradient(145deg, #151518, #0a0a0c)",
                                  borderColor: "rgba(212,175,55,0.12)",
                                }}
                              >
                                <div className="relative aspect-square overflow-hidden [background:radial-gradient(circle_at_40%_25%,rgba(127,13,242,0.22)_0%,transparent_60%),linear-gradient(145deg,#151518,#0a0a0c)]">
                                  {imgUrl ? (
                                    <img
                                      src={imgUrl}
                                      alt={title}
                                      className="absolute inset-0 h-full w-full object-cover"
                                      loading="lazy"
                                    />
                                  ) : null}
                                  <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-black/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-100 backdrop-blur">
                                    <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
                                    <span>{{ meditation: "Meditasyon", breath: "Nefes", frekans: "Frekans", diet: "Diyet", ritual: "Ritüel" }[digitalTab]}</span>
                                  </div>
                                </div>

                                <div className="p-6 text-center">
                                  <p className="font-display text-xl tracking-wide text-slate-100">{title}</p>
                                  {desc ? <p className="mt-1 line-clamp-2 text-xs text-slate-300/80">{desc}</p> : null}
                                  {durationMin ? (
                                    <p className="mt-2 text-[11px] uppercase tracking-[0.16em] opacity-50">{durationMin} dk</p>
                                  ) : null}

                                  <div className="mt-4">
                                    <p className="font-serif text-lg italic tracking-wider" style={{ color: GOLD }}>
                                      {formatTry(item.price)}
                                    </p>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => navigate(`/numeroloji/dijital/${{ meditation: "meditasyon", breath: "nefes", frekans: "frekans", diet: "diyet", ritual: "rituel" }[digitalTab]}/${item.id}`)}
                                    className="mt-5 w-full rounded border py-3 text-xs font-display tracking-[0.22em] text-slate-200 transition-all duration-300 hover:bg-[rgba(212,175,55,1)] hover:text-black"
                                    style={{ borderColor: "rgba(212,175,55,0.40)" }}
                                  >
                                    SATIN AL
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </section>
                )}
              </>
            ) : null}
          </div>
        </div>
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
